import React, { useState, useEffect, useCallback } from 'react';
import {
    fetchCrops,
    fetchSymptomsByLevel,
    fetchNextSymptomsOrDisease,
} from "../api/cropService";
import Tree from "react-d3-tree";
import { useTranslation } from "react-i18next";

const ViewHierarchy = () => {
    const { t } = useTranslation();
    const [crops, setCrops] = useState([]);
    const [selectedCrop, setSelectedCrop] = useState(null);
    const [treeData, setTreeData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [treeKey, setTreeKey] = useState(0);
    const [selectedNodePath, setSelectedNodePath] = useState([]);
    const [diseaseInfo, setDiseaseInfo] = useState(null);

    // Check for mobile screen
    useEffect(() => {
        const checkScreenSize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile) {
                setSidebarOpen(false);
            } else {
                setSidebarOpen(true);
            }
        };
        
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Load crops initially
    useEffect(() => {
        const loadCrops = async () => {
            setIsLoading(true);
            try {
                const data = await fetchCrops(true);
                setCrops(data);
                if (data.length > 0) setSelectedCrop(data[0]);
            } catch (err) {
                console.error(t('expert.viewHierarchy.errorLoadingCrops'), err);
            } finally {
                setIsLoading(false);
            }
        };
        loadCrops();
    }, [t]);

    // Load L1 symptoms when crop changes
    useEffect(() => {
        const loadSymptoms = async () => {
            if (!selectedCrop) return;
            setIsLoading(true);
            try {
                const l1Symptoms = await fetchSymptomsByLevel(selectedCrop.cid, 1, true);

                const buildNode = (symptom, level) => ({
                    name: symptom.name,
                    attributes: { id: symptom.sid, level, type: 'symptom', data: symptom },
                    children: [],
                    _children: [],
                });

                const root = {
                    name: selectedCrop.name,
                    attributes: { id: `crop-${selectedCrop.cid}`, level: 0, type: 'crop' },
                    children: l1Symptoms.map((s) => buildNode(s, 1)),
                    _children: [],
                };

                setTreeData(root);
                setTreeKey(prev => prev + 1);
                setSelectedNodePath([]);
                setDiseaseInfo(null);
            } catch (err) {
                console.error(t('expert.viewHierarchy.errorLoadingSymptoms'), err);
            } finally {
                setIsLoading(false);
            }
        };
        loadSymptoms();
    }, [selectedCrop, t]);

    // Function to get path from root to a specific node
    const getPathToNode = (node, targetId, currentPath = []) => {
        const newPath = [...currentPath, node];
        
        if (node.attributes?.id === targetId) {
            return newPath;
        }

        // Search in visible children
        if (node.children) {
            for (const child of node.children) {
                const result = getPathToNode(child, targetId, newPath);
                if (result) return result;
            }
        }

        // Search in hidden children
        if (node._children) {
            for (const child of node._children) {
                const result = getPathToNode(child, targetId, newPath);
                if (result) return result;
            }
        }

        return null;
    };

    // Fixed function to check for disease mapping
    const checkForDisease = async (nodePath) => {
        if (!selectedCrop || nodePath.length < 2) {
            setDiseaseInfo(null);
            return;
        }
        
        try {
            // Extract symptom IDs from the path (excluding crop node)
            const symptomIds = nodePath
                .filter(node => node.attributes?.type === 'symptom')
                .map(node => node.attributes.id);

            if (symptomIds.length === 0) {
                setDiseaseInfo(null);
                return;
            }

            const result = await fetchNextSymptomsOrDisease(selectedCrop.cid, symptomIds, true);
            console.log("Disease mapping result:", result);
            
            // Check for the correct response structure based on your console log
            if (result && result.diseaseFound === true && result.symptoms && result.symptoms.length > 0) {
                // Look for disease information in the symptoms array or other parts of response
                const diseaseSymptom = result.symptoms.find(s => s.disease || s.name);
                
                setDiseaseInfo({
                    disease: {
                        name: diseaseSymptom?.disease?.name || diseaseSymptom?.name || 'Disease Detected',
                        scientificName: diseaseSymptom?.disease?.scientificName || diseaseSymptom?.scientificName,
                        chemicalName: diseaseSymptom?.disease?.chemicalName || diseaseSymptom?.chemicalName
                    },
                    symptoms: nodePath.filter(node => node.attributes?.type === 'symptom'),
                    otherSymptoms: result.symptoms || [],
                    rawResult: result // Keep raw result for debugging
                });
            } else if (result && result.disease) {
                // Fallback to original structure
                setDiseaseInfo({
                    disease: result.disease,
                    symptoms: nodePath.filter(node => node.attributes?.type === 'symptom'),
                    otherSymptoms: result.otherSymptoms || [],
                    rawResult: result
                });
            } else {
                // No disease found
                setDiseaseInfo(null);
                console.log("No disease found for current symptom combination");
            }
        } catch (err) {
            console.error("Error checking for disease:", err);
            setDiseaseInfo(null);
        }
    };

    // Immutable recursive updater
    const updateTree = (node, id, updater) => {
        if (!node) return node;
        if (node.attributes?.id === id) {
            return updater(node);
        }

        let changed = false;
        let newChildren = undefined;
        if (node.children && node.children.length > 0) {
            newChildren = node.children.map((child) => {
                const updated = updateTree(child, id, updater);
                if (updated !== child) changed = true;
                return updated;
            });
        }

        let newHidden = undefined;
        if (node._children && node._children.length > 0) {
            newHidden = node._children.map((child) => {
                const updated = updateTree(child, id, updater);
                if (updated !== child) changed = true;
                return updated;
            });
        }

        if (changed) {
            return {
                ...node,
                ...(newChildren ? { children: newChildren } : {}),
                ...(newHidden ? { _children: newHidden } : {}),
            };
        }

        return node;
    };

    // Enhanced node click handler with disease mapping
    const handleNodeClick = useCallback(
        async (nodeDatum) => {
            const { id, level, type } = nodeDatum.attributes || {};
            if (!id) return;

            // Update selected path and check for disease
            const pathToNode = getPathToNode(treeData, id);
            if (pathToNode && type === 'symptom') {
                setSelectedNodePath(pathToNode);
                await checkForDisease(pathToNode);
            } else if (type === 'crop') {
                setSelectedNodePath([]);
                setDiseaseInfo(null);
            }

            if (level === 0) return; // don't toggle root crop

            // Existing expand/collapse logic
            if (nodeDatum.children && nodeDatum.children.length > 0) {
                const newTree = updateTree(treeData, id, (n) => ({
                    ...n,
                    _children: n.children || [],
                    children: [],
                }));
                setTreeData(newTree);
                return;
            }

            if (nodeDatum._children && nodeDatum._children.length > 0) {
                const newTree = updateTree(treeData, id, (n) => ({
                    ...n,
                    children: n._children || [],
                    _children: [],
                }));
                setTreeData(newTree);
                return;
            }

            // Fetch next symptoms only if this is a symptom node and we haven't reached a disease
            if (type === 'symptom' && level < 3) {
                try {
                    // Get the path to this node to collect all selected symptoms
                    const pathToNode = getPathToNode(treeData, id);
                    const symptomIds = pathToNode
                        .filter(node => node.attributes?.type === 'symptom')
                        .map(node => node.attributes.id);

                    const resp = await fetchNextSymptomsOrDisease(selectedCrop.cid, symptomIds, true);
                    
                    // Only add child nodes if we got symptoms back (not a disease)
                    if (resp && resp.symptoms && !resp.diseaseFound) {
                        const childrenNodes = resp.symptoms.map((s) => ({
                            name: s.name,
                            attributes: { 
                                id: s.sid,
                                level: level + 1, 
                                type: 'symptom',
                                data: s
                            },
                            children: [],
                            _children: [],
                        }));

                        const newTree = updateTree(treeData, id, (n) => ({
                            ...n,
                            children: childrenNodes,
                            _children: [],
                        }));

                        setTreeData(newTree);
                    }
                } catch (err) {
                    console.error(t('expert.viewHierarchy.errorFetchingChildren'), err);
                }
            }
            
        },
        [selectedCrop, treeData, t]
    );

    // Enhanced node rendering
    const renderNode = ({ nodeDatum }) => {
        const { id, level, type } = nodeDatum.attributes || {};
        const name = nodeDatum.name;
        const hasChildren = (nodeDatum.children && nodeDatum.children.length > 0) ||
                          (nodeDatum._children && nodeDatum._children.length > 0);
        const isExpanded = nodeDatum.children && nodeDatum.children.length > 0;
        const isInSelectedPath = selectedNodePath.some(node => node.attributes?.id === id);
        
        // Responsive node sizing
        const nodeWidth = isMobile ? 100 : 140;
        const nodeHeight = isMobile ? 32 : 40;
        
        // Enhanced color scheme with selection state
        const getNodeColor = (level, isSelected) => {
            const baseColors = {
                0: { bg: '#2563eb', border: '#1d4ed8', text: 'white', shadow: 'rgba(37, 99, 235, 0.3)' },
                1: { bg: '#059669', border: '#047857', text: 'white', shadow: 'rgba(5, 150, 105, 0.3)' },
                2: { bg: '#d97706', border: '#b45309', text: 'white', shadow: 'rgba(217, 119, 6, 0.3)' },
                3: { bg: '#dc2626', border: '#b91c1c', text: 'white', shadow: 'rgba(220, 38, 38, 0.3)' }
            };
            
            const selectedColors = {
                0: { bg: '#1e40af', border: '#1e3a8a', text: 'white', shadow: 'rgba(30, 64, 175, 0.5)' },
                1: { bg: '#047857', border: '#065f46', text: 'white', shadow: 'rgba(4, 120, 87, 0.5)' },
                2: { bg: '#b45309', border: '#92400e', text: 'white', shadow: 'rgba(180, 83, 9, 0.5)' },
                3: { bg: '#b91c1c', border: '#991b1b', text: 'white', shadow: 'rgba(185, 28, 28, 0.5)' }
            };
            
            return isSelected ? selectedColors[level] || selectedColors[3] : baseColors[level] || baseColors[3];
        };

        const nodeColor = getNodeColor(level, isInSelectedPath);

        return (
            <g
                onClick={(e) => {
                    e.stopPropagation?.();
                    handleNodeClick(nodeDatum);
                }}
                style={{ cursor: "pointer" }}
                className="node-group"
            >
                <defs>
                    <filter id={`shadow-${id}`} x="-50%" y="-50%" width="200%" height="200%">
                        <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor={nodeColor.shadow} />
                    </filter>
                    <linearGradient id={`gradient-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={nodeColor.bg} stopOpacity="1" />
                        <stop offset="100%" stopColor={nodeColor.border} stopOpacity="1" />
                    </linearGradient>
                </defs>

                {/* Selection glow */}
                {isInSelectedPath && (
                    <rect
                        width={nodeWidth + 8}
                        height={nodeHeight + 8}
                        x={-(nodeWidth + 8)/2}
                        y={-(nodeHeight + 8)/2}
                        fill="none"
                        stroke={nodeColor.bg}
                        strokeWidth="3"
                        rx={12}
                        ry={12}
                        opacity="0.6"
                        className="animate-pulse"
                    />
                )}

                {/* Main node rectangle */}
                <rect
                    width={nodeWidth}
                    height={nodeHeight}
                    x={-nodeWidth/2}
                    y={-nodeHeight/2}
                    fill={`url(#gradient-${id})`}
                    stroke={nodeColor.border}
                    strokeWidth={isInSelectedPath ? 3 : 2}
                    rx={8}
                    ry={8}
                    filter={`url(#shadow-${id})`}
                    className="transition-all duration-300 hover:scale-105"
                />

                {/* Node text */}
                <text
                    fill={nodeColor.text}
                    x="0"
                    y="0"
                    textAnchor="middle"
                    alignmentBaseline="central"
                    style={{ 
                        fontSize: isMobile ? "10px" : "12px", 
                        fontWeight: level === 0 ? "bold" : isInSelectedPath ? "bold" : "normal",
                        pointerEvents: "none",
                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                    }}
                >
                    {level === 0 ? (name.length > 12 ? name.substring(0, 12) + "..." : name) : id}
                </text>

                {/* Tooltip */}
                <title>{`${t('expert.viewHierarchy.tooltipName')} ${name}\n${t('expert.viewHierarchy.tooltipLevel')} ${level}${isInSelectedPath ? '\n(Selected)' : ''}`}</title>

                {/* Expand/collapse indicator */}
                {hasChildren && (
                    <g className="expand-indicator">
                        <circle
                            cx={nodeWidth/2 - 12}
                            cy={-nodeHeight/2 + 12}
                            r={8}
                            fill="rgba(255, 255, 255, 0.95)"
                            stroke={nodeColor.border}
                            strokeWidth={1.5}
                            className="transition-all duration-200 hover:fill-white"
                        />
                        <text
                            x={nodeWidth/2 - 12}
                            y={-nodeHeight/2 + 12}
                            textAnchor="middle"
                            alignmentBaseline="central"
                            style={{ 
                                fontSize: "8px", 
                                pointerEvents: "none",
                                transition: 'transform 0.3s ease',
                                transformOrigin: `${nodeWidth/2 - 12}px ${-nodeHeight/2 + 12}px`
                            }}
                            transform={isExpanded ? `rotate(90 ${nodeWidth/2 - 12} ${-nodeHeight/2 + 12})` : ''}
                            fill={nodeColor.border}
                        >
                            ▶
                        </text>
                    </g>
                )}
            </g>
        );
    };

    const handleCropSelect = (crop) => {
        setSelectedCrop(crop);
        setSelectedNodePath([]);
        setDiseaseInfo(null);
        if (isMobile) {
            setSidebarOpen(false);
        }
    };

    return (
        <div className="flex flex-col md:flex-row h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            <style jsx>{`
                .node-group {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                
                .node-group:hover {
                    transform: scale(1.05);
                }
                
                .tree-container {
                    background: 
                        radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 75% 75%, rgba(16, 185, 129, 0.1) 0%, transparent 50%);
                }
                
                .sidebar-transition {
                    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                
                .crop-item {
                    transition: all 0.2s ease;
                    position: relative;
                    overflow: hidden;
                }
                
                .crop-item::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
                    transition: left 0.5s;
                }
                
                .crop-item:hover::before {
                    left: 100%;
                }
                
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .animate-fadeInUp {
                    animation: fadeInUp 0.5s ease-out;
                }
                
                .loading-pulse {
                    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                }
                
                @keyframes pulse {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: .5;
                    }
                }
            `}</style>

            {/* Mobile header */}
            {isMobile && (
                <div className="bg-white/90 backdrop-blur-sm shadow-lg p-4 flex justify-between items-center border-b border-gray-200/50 animate-fadeInUp">
                    <div>
                        <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                            Crop Hierarchy
                        </h1>
                        {selectedCrop && (
                            <p className="text-sm text-gray-600 mt-1">{selectedCrop.name}</p>
                        )}
                    </div>
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="p-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                        <span className={`inline-block transition-transform duration-300 ${sidebarOpen ? 'rotate-180' : ''}`}>
                            {sidebarOpen ? '✕' : '☰'}
                        </span>
                    </button>
                </div>
            )}

            {/* LEFT: Tree Visualization */}
            <div className={`${isMobile ? 'flex-1' : 'w-2/3'} tree-container relative overflow-hidden`}>
                {/* Background decorative elements */}
                <div className="absolute inset-0 opacity-30">
                    <div className="absolute top-10 left-10 w-20 h-20 bg-blue-200 rounded-full blur-xl"></div>
                    <div className="absolute bottom-10 right-10 w-32 h-32 bg-indigo-200 rounded-full blur-2xl"></div>
                    <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-green-200 rounded-full blur-lg"></div>
                </div>

                <div className="relative z-10 h-full p-6">
                    {isLoading && (
                        <div className="absolute top-4 right-4 z-20 flex items-center space-x-2 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg">
                            <div className="w-4 h-4 bg-blue-500 rounded-full loading-pulse"></div>
                            <span className="text-sm text-gray-600">Loading...</span>
                        </div>
                    )}

                    {/* Disease mapping panel - Fixed to only show when disease is actually found */}
                    {diseaseInfo && diseaseInfo.disease && (
                        <div className="absolute top-4 left-4 z-20 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg max-w-sm animate-fadeInUp border-l-4 border-red-500">
                            <h3 className="font-bold text-red-700 mb-2">Disease Detected</h3>
                            <div className="text-sm space-y-2">
                                <div>
                                    <span className="font-medium">Disease:</span> {diseaseInfo.disease.name || diseaseInfo.disease.scientificName || 'Identified Disease'}
                                </div>
                                {diseaseInfo.disease.scientificName && diseaseInfo.disease.name && (
                                    <div>
                                        <span className="font-medium">Scientific Name:</span> {diseaseInfo.disease.scientificName}
                                    </div>
                                )}
                                {diseaseInfo.disease.chemicalName && (
                                    <div>
                                        <span className="font-medium">Chemical Name:</span> {diseaseInfo.disease.chemicalName}
                                    </div>
                                )}
                                <div>
                                    <span className="font-medium">Selected Symptoms:</span>
                                    <ul className="list-disc list-inside ml-2 text-xs">
                                        {diseaseInfo.symptoms.map((symptom, idx) => (
                                            <li key={idx}>{symptom.name}</li>
                                        ))}
                                    </ul>
                                </div>
                                {diseaseInfo.otherSymptoms && diseaseInfo.otherSymptoms.length > 0 && (
                                    <div>
                                        <span className="font-medium text-orange-600">Other Related Symptoms:</span>
                                        <ul className="list-disc list-inside ml-2 text-xs text-orange-700">
                                            {diseaseInfo.otherSymptoms.map((symptom, idx) => (
                                                <li key={idx}>{symptom.name}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {/* Debug info - remove in production */}
                                {diseaseInfo.rawResult && console.log("Raw API result:", diseaseInfo.rawResult)}
                            </div>
                        </div>
                    )}

                    {/* Instructions panel */}
                    <div className="absolute bottom-4 left-4 z-20 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-md max-w-xs animate-fadeInUp">
                        <p className="text-xs text-gray-600">
                            <span className="font-medium">Tip:</span> Click on symptoms to see potential disease mappings. Selected symptoms will be highlighted.
                        </p>
                    </div>

                    {treeData ? (
                        <div className="h-full animate-fadeInUp">
                            <Tree
                                key={treeKey}
                                data={treeData}
                                orientation="horizontal"
                                translate={{ 
                                    x: isMobile ? 50 : 100, 
                                    y: (isMobile ? window.innerHeight * 0.4 : window.innerHeight) / 2 
                                }}
                                pathFunc="diagonal"
                                renderCustomNodeElement={renderNode}
                                separation={{ siblings: 1.8, nonSiblings: 2.2 }}
                                nodeSize={{ x: isMobile ? 120 : 180, y: isMobile ? 60 : 80 }}
                                zoom={isMobile ? 0.6 : 0.8}
                                enableLegacyTransitions={true}
                                transitionDuration={500}
                                pathClassFunc={(linkDatum) => {
                                    // Highlight path if both nodes are in selected path
                                    const sourceInPath = selectedNodePath.some(node => node.attributes?.id === linkDatum.source.attributes?.id);
                                    const targetInPath = selectedNodePath.some(node => node.attributes?.id === linkDatum.target.attributes?.id);
                                    return sourceInPath && targetInPath ? 'tree-link selected-path' : 'tree-link';
                                }}
                            />
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center animate-fadeInUp">
                                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full loading-pulse mx-auto mb-4"></div>
                                <p className="text-lg text-gray-600">{t('expert.viewHierarchy.loading')}</p>
                            </div>
                        </div>
                    )}
                </div>

                <style jsx>{`
                    :global(.tree-link) {
                        stroke: #6b7280;
                        stroke-width: 2;
                        fill: none;
                        transition: all 0.3s ease;
                    }
                    
                    :global(.tree-link.selected-path) {
                        stroke: #dc2626;
                        stroke-width: 4;
                    }
                    
                    :global(.tree-link:hover) {
                        stroke: #3b82f6;
                        stroke-width: 3;
                    }
                `}</style>
            </div>

            {/* RIGHT: Enhanced Crop Sidebar with Disease Info */}
            <div className={`
                ${isMobile ? 'fixed inset-y-0 right-0 z-40 w-80' : 'w-1/3'}
                ${isMobile && !sidebarOpen ? 'translate-x-full' : 'translate-x-0'}
                sidebar-transition
            `}>
                {/* Backdrop for mobile */}
                {isMobile && sidebarOpen && (
                    <div 
                        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                <div className="relative z-40 h-full bg-white/95 backdrop-blur-sm border-l border-gray-200/50 shadow-2xl">
                    <div className="h-full flex flex-col">
                        <div className="p-6 border-b border-gray-200/50 bg-gradient-to-r from-white to-gray-50">
                            <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                                {t('expert.viewHierarchy.existingCropsTitle')}
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Select a crop to view its hierarchy
                            </p>
                        </div>

                        {/* Disease Information Panel - Fixed to only show when disease is found */}
                        {diseaseInfo && diseaseInfo.disease && (
                            <div className="p-4 bg-red-50 border-b border-red-200">
                                <h3 className="font-bold text-red-700 mb-2 flex items-center">
                                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                                    Disease Match Found
                                </h3>
                                <div className="space-y-2 text-sm">
                                    <div className="bg-white rounded-lg p-3 border border-red-200">
                                        <div className="font-medium text-red-800">
                                            {diseaseInfo.disease.name || diseaseInfo.disease.scientificName || 'Identified Disease'}
                                        </div>
                                        {diseaseInfo.disease.scientificName && diseaseInfo.disease.name && (
                                            <div className="text-xs text-gray-600 mt-1">
                                                Scientific: {diseaseInfo.disease.scientificName}
                                            </div>
                                        )}
                                        {diseaseInfo.disease.chemicalName && (
                                            <div className="text-xs text-gray-600 mt-1">
                                                Chemical: {diseaseInfo.disease.chemicalName}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div>
                                        <span className="font-medium text-gray-700">Confirming Symptoms:</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {diseaseInfo.symptoms.map((symptom, idx) => (
                                                <span key={idx} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                                    {symptom.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {diseaseInfo.otherSymptoms && diseaseInfo.otherSymptoms.length > 0 && (
                                        <div>
                                            <span className="font-medium text-orange-600">Related Symptoms:</span>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {diseaseInfo.otherSymptoms.map((symptom, idx) => (
                                                    <span key={idx} className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                                                        {symptom.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="flex-1 overflow-y-auto p-4">
                            <div className="space-y-2">
                                {crops.map((crop, index) => (
                                    <div
                                        key={crop.cid}
                                        onClick={() => handleCropSelect(crop)}
                                        className={`crop-item cursor-pointer p-4 rounded-xl transition-all duration-200 transform hover:scale-102 ${
                                            selectedCrop?.cid === crop.cid
                                                ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/25"
                                                : "hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50 border border-gray-200 hover:border-gray-300 hover:shadow-md"
                                        }`}
                                        style={{
                                            animationDelay: `${index * 0.1}s`
                                        }}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium">{crop.name}</span>
                                            {selectedCrop?.cid === crop.cid && (
                                                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Selected Path Information */}
                            {selectedNodePath.length > 1 && (
                                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <h3 className="font-bold text-blue-700 mb-2">Selected Symptom Path</h3>
                                    <div className="space-y-1">
                                        {selectedNodePath
                                            .filter(node => node.attributes?.type === 'symptom')
                                            .map((node, idx) => (
                                                <div key={idx} className="text-sm text-blue-800 flex items-center">
                                                    <span className="w-1 h-1 bg-blue-500 rounded-full mr-2"></span>
                                                    {node.name}
                                                </div>
                                            ))}
                                    </div>
                                    {!diseaseInfo && (
                                        <p className="text-xs text-blue-600 mt-2 italic">
                                            Continue selecting symptoms to find disease matches
                                        </p>
                                    )}
                                </div>
                            )}

                            {crops.length === 0 && !isLoading && (
                                <div className="text-center py-12 animate-fadeInUp">
                                    <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                                        <span className="text-2xl">🌾</span>
                                    </div>
                                    <p className="text-gray-500">No crops available</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewHierarchy;